import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { DeleteIcon, EditIcon } from 'assets';
import { InputField, PopupAddEditAddress, UploadAvatar } from 'components';
import PopupConfirm, { TYPE_ICON } from 'components/popup/PopupConfirm';
import { NotifyService } from 'config/notify';
import yup from 'config/yup.custom';
import { useDebounce } from 'hooks';
import { cloneDeep } from 'lodash';
import { ESIZE_PICTURE, NOTIFY_SETTING_PAYLOAD, USER_TYPE } from 'models';
import { ADDRESS_LIST_RESPONSE } from 'models/parent/address';
import { CHILDREN_LIST_RESPONSE } from 'models/parent/children';
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { profileApi } from 'services';
import { uploadAPIs } from 'services/upload';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { getUserProfileAction } from 'store/reducers/auth/actionTypes';
import { setGlobalLoading } from 'store/reducers/global';
import { updateChildrenList } from 'store/reducers/parent';
import {
  deleteAddressAction,
  deleteChildrenAction,
  getAddressListAction,
  getChildrenListAction,
  updateChildrenAction,
} from 'store/reducers/parent/actionTypes';
import { convertDobToAge } from 'utils';
import PopupAddEditChildren from '../../components/PopupAddEditChildren';
import PopupChangePassword from '../../components/PopupChangePassword';
import { ProfileWrapper } from './styles';

interface IEditProfileForm {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  profilePicture: string;
}

const schema = yup
  .object({
    firstName: yup.string().required('First name is required'),
    lastName: yup.string().required('Last name is required'),
    email: yup.string().email().required('Email is required'),
    phoneNumber: yup.mixed().required('Phone number is required'),
  })
  .required();

export const EditProfile = () => {
  const dispatch = useAppDispatch();
  const { globalLoading } = useAppSelector((state) => state.global);
  const { childrenList, addressList } = useAppSelector((state) => state.parent);
  const { parent } = useAppSelector((state) => state.auth);

  const [openChangePassword, setOpenChangePassword] = useState<boolean>(false);
  const [openAddEditChildren, setOpenAddEditChildren] =
    useState<boolean>(false);
  const [openDeleteChildren, setOpenDeleteChildren] = useState<boolean>(false);
  const selectedChild = useRef<CHILDREN_LIST_RESPONSE>();
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [openAddEditAddress, setOpenAddEditAddress] = useState<boolean>(false);
  const [selectedAddress, setSelectedAddress] =
    useState<ADDRESS_LIST_RESPONSE>();
  const [openDeleteAddress, setOpenDeleteAddress] = useState<boolean>(false);
  const [switchedButton, setSwitchedButton] = useState<boolean | null>(null);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSwitchedButton(event.target.checked);
    const isChecked = event.target.checked;
    changeEmailNotificationSetting(isChecked);
  };

  const { handleSubmit, control, reset, setValue, watch } =
    useForm<IEditProfileForm>({
      mode: 'onChange',
      resolver: yupResolver(schema),
    });

  const changeEmailNotificationSetting = useDebounce(
    async (isChecked: boolean) => {
      const data: NOTIFY_SETTING_PAYLOAD = {
        is_email_notification: isChecked,
      };
      await profileApi
        .notifySetting(parent.parent_id, data)
        .then(() => {
          NotifyService.success('Update successfully');
        })
        .catch((e) => {
          NotifyService.error(e);
        })
        .finally(() => {
          dispatch(setGlobalLoading(false));
        });
    },
    200,
  );

  useEffect(() => {
    reset({
      firstName: parent.first_name || '',
      lastName: parent.last_name || '',
      email: parent.email || '',
      phoneNumber: `${parent.country_code}${parent.phone_number}` || '',
      profilePicture: parent.profile_picture,
    });
    setSwitchedButton(parent.is_email_notification ?? false);
  }, [parent, reset]);

  const onRefreshData = () => {
    dispatch(
      getUserProfileAction({
        data: {
          userType: USER_TYPE.PARENT,
        },
      }),
    );
  };

  const onSubmit = useDebounce(async (data: IEditProfileForm) => {
    if (globalLoading || !parent) return;
    dispatch(setGlobalLoading(true));
    await profileApi
      .updateProfile({
        first_name: data.firstName,
        last_name: data.lastName,
        profile_picture: data.profilePicture,
      })
      .then(() => {
        onRefreshData();
      })
      .catch((e) => {
        NotifyService.error(e);
      })
      .finally(() => {
        dispatch(setGlobalLoading(false));
      });
  }, 500);

  const onChangePicture = async (
    file: File,
    role: USER_TYPE,
    index?: number,
  ) => {
    if (!parent) return;
    dispatch(setGlobalLoading(true));
    const formData = new FormData();
    formData.append('file', file);

    if (role === USER_TYPE.PARENT) {
      await uploadAPIs
        .uploadFile(formData)
        .then(({ data }) => {
          setValue('profilePicture', data?.data.url);
        })
        .catch((e) => {
          NotifyService.error(e);
        })
        .finally(() => {
          dispatch(setGlobalLoading(false));
        });
    }
    if (role === USER_TYPE.CHILDREN) {
      await uploadAPIs
        .uploadFile(formData)
        .then(({ data }) => {
          dispatch(
            updateChildrenAction({
              data: {
                id: selectedChild.current.id,
                picture: data?.data.url,
                full_name: selectedChild.current?.full_name,
                date_of_birth: selectedChild.current.date_of_birth,
              },
              onSuccess: () => {
                const newList = cloneDeep(childrenList);
                newList[index].picture = data?.data.url;
                dispatch(updateChildrenList(newList));
              },
            }),
          );
        })
        .catch((e) => {
          NotifyService.error(e);
        })
        .finally(() => {
          dispatch(setGlobalLoading(false));
        });
    }
  };

  const handleAddChild = () => {
    setIsEdit(false);
    setOpenAddEditChildren(true);
  };

  const handleAddAddress = () => {
    setIsEdit(false);
    setOpenAddEditAddress(true);
  };

  const handleDeleteChild = () => {
    dispatch(
      deleteChildrenAction({
        data: {
          id: selectedChild.current.id,
        },
        onSuccess: () => {
          dispatch(getChildrenListAction());
        },
      }),
    );
  };

  const handleDeleteAddress = () => {
    dispatch(
      deleteAddressAction({
        data: {
          addressId: selectedAddress.id,
        },
        onSuccess: () => {
          dispatch(getAddressListAction());
        },
      }),
    );
  };

  const getAddressInformation = (addressInfo: ADDRESS_LIST_RESPONSE) => {
    const { street, unit_number, block_number, postal_code } = addressInfo;
    return `${unit_number}, ${block_number} ${street}, Singapore ${postal_code}`;
  };

  useEffect(() => {
    onRefreshData();
    dispatch(getChildrenListAction());
    dispatch(getAddressListAction());
    // eslint-disable-next-line
  }, [dispatch]);

  return (
    <ProfileWrapper component="form" onSubmit={handleSubmit(onSubmit)}>
      <Stack mb={3.5} spacing={0.5}>
        <Typography variant="Web_Title_22">Edit Profile</Typography>
        <Typography variant="Web_Label_18" color="text.secondary">
          Edit your profile
        </Typography>
      </Stack>
      <Stack spacing={2.5}>
        <UploadAvatar
          role={USER_TYPE.PARENT}
          onChange={(val) => onChangePicture(val, USER_TYPE.PARENT)}
          value={watch('profilePicture')}
        />
        <Box sx={{ flexGrow: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <InputField
                control={control}
                name="firstName"
                label="First Name*"
                placeholder="First Name"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <InputField
                control={control}
                name="lastName"
                label="Last Name*"
                placeholder="Last Name"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <InputField
                disabled
                control={control}
                name="email"
                label="Email"
                placeholder="Email"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <InputField
                disabled
                control={control}
                name="phoneNumber"
                label="Phone Number"
                placeholder="Phone Number"
              />
            </Grid>
            <Stack pl={2} pt={2}>
              <Typography>Email Notification Setting</Typography>
              <Switch
                sx={{
                  padding: '6px',
                  '& .MuiSwitch-track': {
                    bgcolor: switchedButton
                      ? '#04AA6D !important'
                      : '#D9D9D9 !important',
                    opacity: '1 !important',
                    borderRadius: '12px',
                  },
                  '& .MuiSwitch-thumb': {
                    bgcolor: '#FFFFFF',
                  },
                }}
                checked={switchedButton}
                onChange={handleChange}
              />
            </Stack>
          </Grid>
          <Stack spacing={3} mt={3}>
            <Button
              variant="contained"
              type="submit"
              sx={{ padding: '8px 16px' }}
            >
              Save Profile
            </Button>
          </Stack>
        </Box>
        <Divider sx={{ borderColor: '#D9D9D9' }} />
        <Stack spacing={1}>
          <Stack
            direction={'row'}
            justifyContent={'space-between'}
            alignItems={'center'}
          >
            <Typography fontSize={15} fontWeight={400}>
              My Children
            </Typography>
            <Button onClick={handleAddChild} sx={{ padding: 0, height: 30 }}>
              Add Children
            </Button>
          </Stack>
          <Stack spacing={2}>
            {childrenList.map((child, index) => (
              <Stack
                direction="row"
                justifyContent={'space-between'}
                alignItems="center"
                key={child.id}
                onClick={() => {
                  selectedChild.current = child;
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <UploadAvatar
                    role={USER_TYPE.CHILDREN}
                    sizePicture={ESIZE_PICTURE.SMALL}
                    value={child.picture}
                    onChange={(val) =>
                      onChangePicture(val, USER_TYPE.CHILDREN, index)
                    }
                  />
                  <Stack spacing={0.25}>
                    <Typography variant="Title_Menu_14">
                      {child.full_name}
                    </Typography>
                    <Typography variant="Small_Body_12">
                      {convertDobToAge(child.date_of_birth)}
                    </Typography>
                  </Stack>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <IconButton
                    sx={{ color: 'primary.main', p: 0.75 }}
                    onClick={() => {
                      setIsEdit(true);
                      setOpenAddEditChildren(true);
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    sx={{ color: 'light.secondary', p: 0.75 }}
                    onClick={() => {
                      selectedChild.current = child;
                      setOpenDeleteChildren(true);
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Stack>
              </Stack>
            ))}
          </Stack>
        </Stack>
        <Divider sx={{ borderColor: '#D9D9D9' }} />
        <Stack spacing={1}>
          <Stack
            direction={'row'}
            justifyContent={'space-between'}
            alignItems={'center'}
          >
            <Typography fontSize={15} fontWeight={400}>
              My Address
            </Typography>
            <Button onClick={handleAddAddress} sx={{ padding: 0, height: 30 }}>
              Add Address
            </Button>
          </Stack>
          <Stack spacing={2}>
            {addressList.map((item) => (
              <Stack
                direction="row"
                justifyContent={'space-between'}
                alignItems="center"
                key={item.id}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <Stack spacing={0.25}>
                    <Typography variant="Title_Menu_14">{item.name}</Typography>
                    <Typography variant="Small_Body_12">
                      {getAddressInformation(item)}
                    </Typography>
                  </Stack>
                </Stack>
                <Stack direction="row" alignItems="center">
                  <IconButton
                    sx={{ color: 'primary.main', p: 0.75 }}
                    onClick={() => {
                      setSelectedAddress(item);
                      setIsEdit(true);
                      setOpenAddEditAddress(true);
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    sx={{ color: 'light.secondary', p: 0.75 }}
                    onClick={() => {
                      setSelectedAddress(item);
                      setOpenDeleteAddress(true);
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Stack>
              </Stack>
            ))}
          </Stack>
          <Stack></Stack>
        </Stack>
      </Stack>

      <PopupChangePassword
        openPop={openChangePassword}
        onClose={() => setOpenChangePassword(false)}
      />
      {openAddEditChildren && (
        <PopupAddEditChildren
          isEdit={isEdit}
          selectedChildren={selectedChild.current}
          openPop={openAddEditChildren}
          onClose={() => setOpenAddEditChildren(false)}
        />
      )}
      {openAddEditAddress && (
        <PopupAddEditAddress
          open={openAddEditAddress}
          onClose={() => setOpenAddEditAddress(false)}
          isEdit={isEdit}
          itemSelected={selectedAddress}
        />
      )}
      {openDeleteAddress && (
        <PopupConfirm
          typeIcon={TYPE_ICON.HELP}
          title="Delete a Address"
          content={
            <Typography>Are you sure you want to delete this entry?</Typography>
          }
          textButton2="Back"
          onSubmit={handleDeleteAddress}
          handleClose={() => setOpenDeleteAddress(false)}
        />
      )}
      {openDeleteChildren && (
        <PopupConfirm
          typeIcon={TYPE_ICON.HELP}
          title="Delete a Child"
          content={
            <Typography>Are you sure you want to delete this entry?</Typography>
          }
          textButton2="Back"
          onSubmit={handleDeleteChild}
          handleClose={() => setOpenDeleteChildren(false)}
        />
      )}
    </ProfileWrapper>
  );
};

export default EditProfile;
